import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import { API_URL } from '../../config';
import EmailVerifyForm from './EmailVerifyForm';
import AuthShell from './AuthShell';

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null); // non-null => compte pas encore verifie

  const attemptLogin = async () => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await attemptLogin();

      if (data.success) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_type', data.user_type);
        localStorage.setItem('user_id', data.user_id);
        localStorage.setItem('user_name', data.name);
        onLogin(data);
      } else if (data.error_code === 'email_not_verified') {
        setPendingUserId(data.user_id);
      } else {
        setError(data.message || 'Erreur de connexion');
      }
    } catch (err) {
      setError('Impossible de se connecter au serveur');
    }

    setLoading(false);
  };

  const handleVerified = async () => {
    try {
      const data = await attemptLogin();
      if (data.success) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_type', data.user_type);
        localStorage.setItem('user_id', data.user_id);
        localStorage.setItem('user_name', data.name);
        onLogin(data);
      } else {
        setPendingUserId(null);
        setError(data.message || 'Erreur de connexion');
      }
    } catch (err) {
      setPendingUserId(null);
      setError('Impossible de se connecter au serveur');
    }
  };

  return (
    <AuthShell title="Agent TeckiA" subtitle="Agent Intelligent Polyvalent">
        <div className="au-card">
          {pendingUserId ? (
            <EmailVerifyForm
              userId={pendingUserId}
              email={email}
              onVerified={handleVerified}
              onCancel={() => setPendingUserId(null)}
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email ou téléphone
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="votre@email.com ou 034 12 345 67"
                required
              />
            </div>

            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="au-btn w-full disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>
          )}

          {!pendingUserId && (
          <div className="text-center mt-6">
            <span className="text-blue-200/60 text-sm">Pas encore de compte ? </span>
            <button
              onClick={() => navigate('/signup')}
              className="text-blue-300 hover:text-blue-200 text-sm font-semibold transition-colors"
            >
              Créer un compte gratuit
            </button>
          </div>
          )}
        </div>
    </AuthShell>
  );
}
